import type { FC } from 'react'
import {
	Box,
	Heading,
	Link as ChakraLink,
	UnorderedList,
	ListItem,
} from '@chakra-ui/react'
import type { ChakraProps } from '@chakra-ui/react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
	getIsEnhancedSERPUrl,
	getEnhancedSERPOfficeType,
} from './enhancementutils'
import type { AreaURLMap } from './enhancementutils'

interface CityLinksProps {
	areaUrl: string
}

type URLItem = { name: string; url: string }
const STHLM_CITY: URLItem = {
	name: 'City',
	url: 'stockholm/city',
}
const GAMLA_STAN: URLItem = {
	name: 'Gamla Stan',
	url: 'stockholm/gamla-stan',
}
const OSTERMALM: URLItem = {
	name: 'Östermalm',
	url: 'stockholm/östermalm',
}
const SODERMALM: URLItem = {
	name: 'Södermalm',
	url: 'stockholm/södermalm',
}
const VASASTAN: URLItem = {
	name: 'Vasastan',
	url: 'stockholm/vasastan',
}
const KUNGSHOLMEN: URLItem = {
	name: 'Kungsholmen',
	url: 'stockholm/kungsholmen',
}
const KISTA: URLItem = {
	name: 'Kista',
	url: 'stockholm/kista',
}
const BROMMA: URLItem = {
	name: 'Bromma',
	url: 'stockholm/bromma',
}
const HAMMARBY: URLItem = {
	name: 'Hammarby',
	url: 'stockholm/hammarby-sjöstad',
}
const SODERORT: URLItem = {
	name: 'Söderort',
	url: 'stockholm/söderort',
}
const NACKA: URLItem = {
	name: 'Nacka',
	url: 'stockholm/nacka',
}
const GBG_CITY: URLItem = {
	name: 'Göteborg City',
	url: 'göteborg/göteborg_city',
}
const INOM_VALLGRAVEN: URLItem = {
	name: 'Inom Vallgraven',
	url: 'göteborg/inom-vallgraven',
}
const LINDHOLMEN: URLItem = {
	name: 'Lindholmen',
	url: 'göteborg/lindholmen',
}
const MOLNDAL: URLItem = {
	name: 'Mölndal',
	url: 'göteborg/mölndal',
}
const NORDOST: URLItem = {
	name: 'Nordost',
	url: 'göteborg/nordost',
}
const STAMPEN: URLItem = {
	name: 'Stampen',
	url: 'göteborg/stampen',
}
const PARTILLE: URLItem = {
	name: 'Partille',
	url: 'göteborg/partille',
}
const MALMO_CITY: URLItem = {
	name: 'Malmö City',
	url: 'malmö/malmö-city',
}
const HYLLIE: URLItem = {
	name: 'Hyllie',
	url: 'malmö/hyllie',
}
const GAMLA_STADEN: URLItem = {
	name: 'Gamla Staden',
	url: 'area=R3050021',
}
const VASTRA_HAMNEN: URLItem = {
	name: 'Västra Hamnen',
	url: 'area=R3050114',
}
const INRE_HAMNEN: URLItem = {
	name: 'Inre Hamnen',
	url: 'area=R3050036',
}
const RADMANSVANGEN: URLItem = {
	name: 'Rådmansvången',
	url: 'area=R3050083',
}

const AREA_URLS: AreaURLMap = {
	stockholm: {
		office: [
			{ name: STHLM_CITY.name, url: STHLM_CITY.url },
			{ name: OSTERMALM.name, url: OSTERMALM.url },
			{ name: SODERMALM.name, url: SODERMALM.url },
			{ name: KUNGSHOLMEN.name, url: KUNGSHOLMEN.url },
			{ name: KISTA.name, url: KISTA.url },
			{ name: BROMMA.name, url: BROMMA.url },
			{ name: HAMMARBY.name, url: HAMMARBY.url },
		],
		serviced_office: [
			{ name: STHLM_CITY.name, url: STHLM_CITY.url },
			{ name: OSTERMALM.name, url: OSTERMALM.url },
			{ name: SODERMALM.name, url: SODERMALM.url },
			{ name: KUNGSHOLMEN.name, url: KUNGSHOLMEN.url },
			{ name: KISTA.name, url: KISTA.url },
			{ name: BROMMA.name, url: BROMMA.url },
			{ name: HAMMARBY.name, url: HAMMARBY.url },
		],
		coworking_space: [
			{ name: STHLM_CITY.name, url: STHLM_CITY.url },
			{ name: OSTERMALM.name, url: OSTERMALM.url },
			{ name: SODERMALM.name, url: SODERMALM.url },
			{ name: KUNGSHOLMEN.name, url: KUNGSHOLMEN.url },
			{ name: KISTA.name, url: KISTA.url },
			{ name: BROMMA.name, url: BROMMA.url },
			{ name: HAMMARBY.name, url: HAMMARBY.url },
		],
	},
	göteborg: {
		office: [
			{ name: INOM_VALLGRAVEN.name, url: INOM_VALLGRAVEN.url },
			{ name: LINDHOLMEN.name, url: LINDHOLMEN.url },
			{ name: GBG_CITY.name, url: GBG_CITY.url },
		],
		serviced_office: [
			{ name: LINDHOLMEN.name, url: LINDHOLMEN.url },
			{ name: GBG_CITY.name, url: GBG_CITY.url },
			{ name: MOLNDAL.name, url: MOLNDAL.url },
			{ name: NORDOST.name, url: NORDOST.url },
		],
		coworking_space: [
			{ name: STAMPEN.name, url: STAMPEN.url },
			{ name: PARTILLE.name, url: PARTILLE.url },
			{ name: GBG_CITY.name, url: GBG_CITY.url },
			{ name: LINDHOLMEN.name, url: LINDHOLMEN.url },
		],
	},
	malmö: {
		office: [
			{ name: HYLLIE.name, url: HYLLIE.url },
			{ name: GAMLA_STADEN.name, url: GAMLA_STADEN.url },
			{ name: MALMO_CITY.name, url: MALMO_CITY.url },
			{ name: VASTRA_HAMNEN.name, url: VASTRA_HAMNEN.url },
		],
		serviced_office: [
			{ name: MALMO_CITY.name, url: MALMO_CITY.url },
			{ name: VASTRA_HAMNEN.name, url: VASTRA_HAMNEN.url },
			{ name: HYLLIE.name, url: HYLLIE.url },
			{ name: INRE_HAMNEN.name, url: INRE_HAMNEN.url },
			{ name: RADMANSVANGEN.name, url: RADMANSVANGEN.url },
		],
		coworking_space: [
			{ name: MALMO_CITY.name, url: MALMO_CITY.url },
			{ name: VASTRA_HAMNEN.name, url: VASTRA_HAMNEN.url },
			{ name: HYLLIE.name, url: HYLLIE.url },
			{ name: RADMANSVANGEN.name, url: RADMANSVANGEN.url },
		],
	},
	'stockholm/östermalm': {
		office: [
			{ name: STHLM_CITY.name, url: STHLM_CITY.url },
			{ name: SODERMALM.name, url: SODERMALM.url },
			{ name: VASASTAN.name, url: VASASTAN.url },
			{ name: KUNGSHOLMEN.name, url: KUNGSHOLMEN.url },
		],
		serviced_office: [
			{ name: STHLM_CITY.name, url: STHLM_CITY.url },
			{ name: SODERMALM.name, url: SODERMALM.url },
			{ name: VASASTAN.name, url: VASASTAN.url },
			{ name: KUNGSHOLMEN.name, url: KUNGSHOLMEN.url },
		],
		coworking_space: [
			{ name: STHLM_CITY.name, url: STHLM_CITY.url },
			{ name: SODERMALM.name, url: SODERMALM.url },
			{ name: VASASTAN.name, url: VASASTAN.url },
			{ name: KUNGSHOLMEN.name, url: KUNGSHOLMEN.url },
		],
	},
	'stockholm/södermalm': {
		office: [
			{ name: STHLM_CITY.name, url: STHLM_CITY.url },
			{ name: OSTERMALM.name, url: OSTERMALM.url },
			{ name: KUNGSHOLMEN.name, url: KUNGSHOLMEN.url },
			{ name: HAMMARBY.name, url: HAMMARBY.url },
		],
		serviced_office: [
			{ name: STHLM_CITY.name, url: STHLM_CITY.url },
			{ name: OSTERMALM.name, url: OSTERMALM.url },
			{ name: GAMLA_STAN.name, url: GAMLA_STAN.url },
			{ name: SODERORT.name, url: SODERORT.url },
			{ name: NACKA.name, url: NACKA.url },
		],
		coworking_space: [
			{ name: STHLM_CITY.name, url: STHLM_CITY.url },
			{ name: OSTERMALM.name, url: OSTERMALM.url },
			{ name: GAMLA_STAN.name, url: GAMLA_STAN.url },
			{ name: SODERORT.name, url: SODERORT.url },
		],
	},
}

const useOfficeTypeTranslation = (mainPath: string | undefined) => {
	const { t } = useTranslation()
	if (mainPath == t('coworking-spaces', { ns: 'url' })) {
		return t('Coworking Spaces')
	}
	if (mainPath == t('lediga-kontorshotell', { ns: 'url' })) {
		return t('Kontorshotell')
	}
	return t('Kontor')
}

const CityLinks: FC<CityLinksProps & ChakraProps> = ({ areaUrl, ...rest }) => {
	const { t, i18n } = useTranslation()
	const { pathname } = useLocation()
	const pathnames = pathname.split('/')
	const mainPathname =
		i18n.language == 'sv' ? pathnames[1] : `${i18n.language}/${pathnames[2]!}`
	const officeTypeInPath = i18n.language == 'sv' ? pathnames[1] : pathnames[2]!
	const officeType = useOfficeTypeTranslation(officeTypeInPath)
	if (!areaUrl || !mainPathname) {
		return null
	}

	const enhancedOfficeType = getEnhancedSERPOfficeType(
		officeTypeInPath || '',
		t
	)
	if (!getIsEnhancedSERPUrl(areaUrl) || !enhancedOfficeType) {
		return null
	}

	return (
		<Box {...rest}>
			<Heading mb="1rem" size={{ base: 'sm', md: 'md' }}>
				{t('Populära sökningar')}
			</Heading>
			<UnorderedList display="flex" flexWrap="wrap" gap={3} ml={0}>
				{AREA_URLS[areaUrl][enhancedOfficeType].map((area, i) => (
					<ListItem key={`area-link-${i}`} pl={0} sx={{ listStyle: 'none' }}>
						<ChakraLink
							as={Link}
							to={`/${mainPathname}${area.url.includes('area=') ? '?' : '/'}${
								area.url
							}`}
						>
							{`${officeType} ${area.name}`}
						</ChakraLink>
					</ListItem>
				))}
			</UnorderedList>
		</Box>
	)
}

export default CityLinks
